import React, { useState, useRef } from 'react';
import { FileText, CheckCircle, AlertTriangle, Loader2, Upload, Download, Mail, Paperclip } from 'lucide-react';
import { proposalGenerator, type ProposalTemplate, type ProposalSection } from '../../lib/api/ai/proposal';
import { complianceChecker, type ComplianceReport } from '../../lib/api/ai/compliance';
import { Modal } from '../ui/Modal';
import { toast } from 'react-hot-toast';

interface ProposalGeneratorProps {
  contractId: string;
  onComplete: (proposal: ProposalTemplate) => void;
}

export function AIProposalGenerator({ contractId, onComplete }: ProposalGeneratorProps) {
  const [generating, setGenerating] = useState(false);
  const [currentSection, setCurrentSection] = useState<string | null>(null);
  const [proposal, setProposal] = useState<ProposalTemplate | null>(null);
  const [compliance, setCompliance] = useState<ComplianceReport | null>(null);
  const [pendingAttachments, setPendingAttachments] = useState<Array<{
    id: string;
    type: 'image' | 'file';
    name: string;
    url: string;
    size: string;
  }>>([]);

  const fileInputRef = useRef<HTMLInputElement>(null);
  const imageInputRef = useRef<HTMLInputElement>(null);

  const handleGenerate = async () => {
    setGenerating(true);
    try {
      const contract = { id: contractId, title: 'Sample Contract' };
      const company = { name: 'Sample Company' };
      const requirements = ['Requirement 1', 'Requirement 2'];

      const generatedProposal = await proposalGenerator.generateProposal(contract, company, requirements);
      const complianceReport = await complianceChecker.checkCompliance(
        JSON.stringify(generatedProposal),
        requirements
      );

      setProposal(generatedProposal);
      setCompliance(complianceReport);
      onComplete(generatedProposal);
      toast.success('Proposal generated successfully');
    } catch (error) {
      console.error('Failed to generate proposal:', error);
      toast.error('Failed to generate proposal');
    } finally {
      setGenerating(false);
    }
  };

  const handleImproveSection = async (section: ProposalSection) => {
    setCurrentSection(section.title);
    try {
      const improvedSection = await proposalGenerator.improveSection(section);
      if (proposal) {
        setProposal({
          ...proposal,
          sections: proposal.sections.map((s) =>
            s.title === section.title ? improvedSection : s
          ),
        });
        toast.success(`Improved ${section.title} section`);
      }
    } catch (error) {
      console.error('Failed to improve section:', error);
      toast.error(`Failed to improve ${section.title} section`);
    } finally {
      setCurrentSection(null);
    }
  };

  const handleFileSelect = async (e: React.ChangeEvent<HTMLInputElement>, type: 'image' | 'file') => {
    const files = e.target.files;
    if (!files?.length) return;

    const newAttachments = Array.from(files).map((file) => {
      const url = URL.createObjectURL(file);
      return {
        id: crypto.randomUUID(),
        type,
        name: file.name,
        url,
        size: `${(file.size / 1024).toFixed(1)} KB`,
      };
    });

    setPendingAttachments((prev) => [...prev, ...newAttachments]);
    toast.success(`${newAttachments.length} file(s) attached successfully`);

    // Reset file inputs
    if (fileInputRef.current) fileInputRef.current.value = '';
    if (imageInputRef.current) imageInputRef.current.value = '';
  };

  const removeAttachment = (id: string) => {
    setPendingAttachments((prev) => {
      const attachment = prev.find((a) => a.id === id);
      if (attachment) URL.revokeObjectURL(attachment.url);
      return prev.filter((a) => a.id !== id);
    });
  };

  return (
    <div className="space-y-6">
      {!proposal ? (
        <div className="rounded-lg border border-gray-200 bg-white p-6 text-center">
          <FileText className="mx-auto h-12 w-12 text-gray-400" />
          <h3 className="mt-2 text-sm font-medium text-gray-900">No proposal generated yet</h3>
          <p className="mt-1 text-sm text-gray-500">Generate a new proposal using AI assistance or upload existing files</p>
          <div className="mt-6 flex justify-center space-x-4">
            <input
              type="file"
              ref={fileInputRef}
              onChange={(e) => handleFileSelect(e, 'file')}
              className="hidden"
              multiple
              accept=".pdf,.doc,.docx,.txt"
            />
            <button
              onClick={() => fileInputRef.current?.click()}
              className="inline-flex items-center rounded-md border border-gray-300 bg-white px-4 py-2 text-sm font-medium text-gray-700 hover:bg-gray-50"
            >
              <Upload className="mr-2 h-4 w-4" />
              Upload Files
            </button>
            <button
              onClick={handleGenerate}
              disabled={generating}
              className="inline-flex items-center rounded-md bg-indigo-600 px-4 py-2 text-sm font-medium text-white hover:bg-indigo-700"
            >
              {generating ? (
                <>
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                  Generating...
                </>
              ) : (
                'Generate Proposal'
              )}
            </button>
          </div>
        </div>
      ) : (
        <div className="space-y-6">
          {/* Existing Proposal Actions */}
          <div className="flex justify-end space-x-3">
            <button
              onClick={() => fileInputRef.current?.click()}
              className="inline-flex items-center rounded-md border border-gray-300 bg-white px-4 py-2 text-sm font-medium text-gray-700 hover:bg-gray-50"
            >
              <Upload className="mr-2 h-4 w-4" />
              Upload Files
            </button>
            <button
              onClick={() => {
                const blob = new Blob([JSON.stringify(proposal, null, 2)], { type: 'application/json' });
                const url = URL.createObjectURL(blob);
                const a = document.createElement('a');
                a.href = url;
                a.download = 'proposal.json';
                a.click();
                URL.revokeObjectURL(url);
              }}
              className="inline-flex items-center rounded-md border border-gray-300 bg-white px-4 py-2 text-sm font-medium text-gray-700 hover:bg-gray-50"
            >
              <Download className="mr-2 h-4 w-4" />
              Download
            </button>
            <button
              onClick={() => toast.success('Share functionality coming soon')}
              className="inline-flex items-center rounded-md bg-indigo-600 px-4 py-2 text-sm font-medium text-white hover:bg-indigo-700"
            >
              <Mail className="mr-2 h-4 w-4" />
              Share
            </button>
          </div>

          {/* Proposal and Compliance Sections */}
          {proposal.sections.map((section) => (
            <SectionCard
              key={section.title}
              section={section}
              currentSection={currentSection}
              onImproveSection={handleImproveSection}
            />
          ))}
        </div>
      )}
    </div>
  );
}
